import { useEffect, useMemo, useState } from 'react'
import { Input } from '@/components/ui/input'
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '@/components/ui/select'
import { MultiSelect } from '@/components/ui/multi-select'
import {
  useBuyStrategyCodes,
  useConsolidatedDcs,
} from '@/hooks/useCltReference'

export interface EventFilters {
  dc?: string
  origin_port?: string
  is_import?: boolean
  is_rail?: boolean
  strategy_codes?: string[]
}

interface EventFiltersBarProps {
  value: EventFilters
  onChange: (next: EventFilters) => void
}

const ANY = '__any__'

function triValue(v: boolean | undefined): string {
  if (v === undefined) return ANY
  return v ? 'yes' : 'no'
}

function parseTri(v: string): boolean | undefined {
  if (v === 'yes') return true
  if (v === 'no') return false
  return undefined
}

const hasAnyFilter = (f: EventFilters): boolean =>
  Boolean(
    f.dc ||
    f.origin_port ||
    f.is_import !== undefined ||
    f.is_rail !== undefined ||
    f.strategy_codes?.length
  )

export function EventFiltersBar({ value, onChange }: EventFiltersBarProps) {
  const { data: dcs = [] } = useConsolidatedDcs()
  const { data: strategyCodes = [] } = useBuyStrategyCodes()

  // Debounce origin_port input so we don't refetch on every keystroke.
  const [originDraft, setOriginDraft] = useState(value.origin_port ?? '')
  useEffect(() => {
    setOriginDraft(value.origin_port ?? '')
  }, [value.origin_port])
  useEffect(() => {
    const trimmed = originDraft.trim()
    const next = trimmed ? trimmed : undefined
    if (next === value.origin_port) return
    const t = setTimeout(() => onChange({ ...value, origin_port: next }), 250)
    return () => clearTimeout(t)
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [originDraft])

  const dcOptions = useMemo(() => {
    const seen = new Set<string>()
    const out: { code: string; label: string }[] = []
    for (const d of dcs) {
      if (seen.has(d.dc_code)) continue
      seen.add(d.dc_code)
      out.push({ code: d.dc_code, label: d.dc_code })
    }
    return out.sort((a, b) => a.code.localeCompare(b.code))
  }, [dcs])

  const strategyOptions = useMemo(
    () =>
      strategyCodes
        .map(s => ({ value: s.code, label: s.code }))
        .sort((a, b) => a.value.localeCompare(b.value)),
    [strategyCodes]
  )

  const setField = <K extends keyof EventFilters>(
    key: K,
    next: EventFilters[K]
  ) => {
    onChange({ ...value, [key]: next })
  }

  return (
    <div className="rounded-lg border border-border bg-card p-3">
      <div className="flex flex-wrap items-end gap-3">
        <FilterField label="DC" className="min-w-[140px]">
          <Select
            value={value.dc ?? ANY}
            onValueChange={v => setField('dc', v === ANY ? undefined : v)}
          >
            <SelectTrigger className="h-9">
              <SelectValue placeholder="Any DC" />
            </SelectTrigger>
            <SelectContent>
              <SelectItem value={ANY}>Any DC</SelectItem>
              {dcOptions.map(d => (
                <SelectItem key={d.code} value={d.code}>
                  {d.label}
                </SelectItem>
              ))}
            </SelectContent>
          </Select>
        </FilterField>

        <FilterField label="Origin port" className="min-w-[140px]">
          <Input
            value={originDraft}
            onChange={e => setOriginDraft(e.target.value)}
            placeholder="Any"
            className="h-9"
          />
        </FilterField>

        <FilterField label="Sourcing" className="min-w-[120px]">
          <Select
            value={triValue(value.is_import)}
            onValueChange={v => setField('is_import', parseTri(v))}
          >
            <SelectTrigger className="h-9">
              <SelectValue />
            </SelectTrigger>
            <SelectContent>
              <SelectItem value={ANY}>Any</SelectItem>
              <SelectItem value="yes">Import</SelectItem>
              <SelectItem value="no">Domestic</SelectItem>
            </SelectContent>
          </Select>
        </FilterField>

        <FilterField label="Mode" className="min-w-[120px]">
          <Select
            value={triValue(value.is_rail)}
            onValueChange={v => setField('is_rail', parseTri(v))}
          >
            <SelectTrigger className="h-9">
              <SelectValue />
            </SelectTrigger>
            <SelectContent>
              <SelectItem value={ANY}>Any</SelectItem>
              <SelectItem value="yes">Rail</SelectItem>
              <SelectItem value="no">No rail</SelectItem>
            </SelectContent>
          </Select>
        </FilterField>

        <FilterField label="Strategy codes" className="min-w-[240px] flex-1">
          <MultiSelect
            options={strategyOptions}
            value={value.strategy_codes ?? []}
            onValueChange={v =>
              setField('strategy_codes', v.length ? v : undefined)
            }
            placeholder="Any"
            className="h-9"
          />
        </FilterField>

        {hasAnyFilter(value) && (
          <button
            type="button"
            onClick={() => onChange({})}
            className="ml-auto h-9 cursor-pointer text-xs text-muted-foreground underline-offset-2 hover:text-foreground hover:underline"
          >
            Clear filters
          </button>
        )}
      </div>
    </div>
  )
}

interface FilterFieldProps {
  label: string
  className?: string
  children: React.ReactNode
}

function FilterField({ label, className, children }: FilterFieldProps) {
  return (
    <div className={`flex flex-col gap-1 ${className ?? ''}`}>
      <label className="text-[11px] font-medium uppercase tracking-wide text-muted-foreground">
        {label}
      </label>
      {children}
    </div>
  )
}
